import { type UserProfile, type UserRole } from '@/types/team';

export const PERMISSIONS = {
    // Org Management
    MANAGE_ORG: ['OWNER'],
    MANAGE_OFFICES: ['OWNER', 'ORG_ADMIN'],
    MANAGE_USERS: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER'],

    // View Permissions
    VIEW_ORG_SETTINGS: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN'],
    VIEW_ALL_USERS: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER'],
    VIEW_FULL_TEAM_OPS: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER'],

    // Job Management
    CREATE_JOB: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER'],
    ASSIGN_JOB: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER'],
    DELETE_JOB: ['OWNER', 'ORG_ADMIN'],

    // Content
    ADD_NOTES: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],
    UPLOAD_PHOTOS: ['OWNER', 'ORG_ADMIN', 'OFFICE_ADMIN', 'DEPT_MANAGER', 'MEMBER'],

    // Billing (WEB-75)
    // VIEW_BILLING: the Firestore-mirrored billing data /billing renders
    // (billing/{orgId}, its stripe_invoices and token_usage, the org's
    // stripe_meter_outbox). Mirrors isBillingReader() in firestore.rules.
    VIEW_BILLING: ['OWNER', 'ORG_ADMIN', 'FINANCE'],
    // MANAGE_BILLING: everything that goes through the stripeBilling Cloud
    // Function - plan change, portal, payment method AND its GET routes
    // (subscription, latest invoice, meter timeline). Mirrors BILLING_ROLES in
    // functions/src/billingStripe/billingAuth.ts; a parity test
    // (useRBAC.test.ts) fails when the two drift, so widening the function to
    // FINANCE widens this list in the same change, and the controls the
    // function would refuse stay hidden until then.
    MANAGE_BILLING: ['OWNER', 'ORG_ADMIN'],
} as const;

export type Permission = keyof typeof PERMISSIONS;

/**
 * The only permissions a platform admin keeps while viewing another org
 * (src/lib/platformAdminState.ts). Everything else - every MANAGE_, CREATE_,
 * ASSIGN_, DELETE_, ADD_ and UPLOAD_ permission - is false, matching the rules,
 * which admit that view to reads only. VIEW_BILLING is out too: billing data is
 * not opened to the view.
 */
export const PLATFORM_ADMIN_VIEW_PERMISSIONS: readonly Permission[] = [
    'VIEW_ORG_SETTINGS',
    'VIEW_ALL_USERS',
    'VIEW_FULL_TEAM_OPS',
];

export const hasPermission = (profile: UserProfile | null, permission: Permission): boolean => {
    if (!profile) return false;
    if (profile.platformAdminView) return PLATFORM_ADMIN_VIEW_PERMISSIONS.includes(permission);
    const roles = PERMISSIONS[permission] as readonly UserRole[];
    return roles.includes(profile.role);
};

/**
 * WEB-75: roles that may open a job but never act on it. The job page, list
 * and board hide every create, edit, delete, status, assignment, scope, line
 * item and ESX control for these roles; firestore.rules refuses the writes
 * (canReadJob admits FINANCE to reads, canAccessJob does not admit it to
 * writes).
 */
export const isJobReadOnlyRole = (role: UserRole | undefined | null): boolean => role === 'FINANCE';

/**
 * Read-only on the job surfaces: a read-only role (FINANCE), or a platform
 * admin viewing another org. The job page, list, board and cards use this.
 */
export const isJobReadOnlyProfile = (profile: Pick<UserProfile, 'role' | 'platformAdminView'> | null | undefined): boolean =>
    !!profile?.platformAdminView || isJobReadOnlyRole(profile?.role);

export const canViewJob = (profile: UserProfile | null, job: {
    orgId: string;
    officeId: string;
    departmentId: string;
    assignedUserIds: string[];
    createdBy: string;
}): boolean => {
    if (!profile) return false;

    // OWNER/ORG_ADMIN see everything in the org; FINANCE reads everything in the org
    if (profile.role === 'OWNER' || profile.role === 'ORG_ADMIN' || profile.role === 'FINANCE') {
        return profile.orgId === job.orgId;
    }

    // OFFICE_ADMIN sees everything in their office
    if (profile.role === 'OFFICE_ADMIN') {
        return profile.orgId === job.orgId && profile.officeId === job.officeId;
    }

    // DEPT_MANAGER sees everything in their department
    if (profile.role === 'DEPT_MANAGER') {
        return profile.orgId === job.orgId &&
            profile.officeId === job.officeId &&
            profile.departmentId === job.departmentId;
    }

    // MEMBER sees only assigned or created jobs
    if (profile.role === 'MEMBER') {
        return job.assignedUserIds.includes(profile.uid) || job.createdBy === profile.uid;
    }

    return false;
};
